import { useForm, useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, TextInput, Select, NativeSelect, Group, Button, SimpleGrid, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { BILLING_CONSTRAINTS } from '@/lib/constants/account';
import { CountryCodes } from '@lib2/model/form/CountryCodes';
import { paymentMethodSchema, type PaymentMethodFormValues } from '../../schemas/billingSchemas';
import type { IBillingAccountViewData } from '@/lib/types/Billing';

const COUNTRY_OPTIONS = CountryCodes.ISO2Labels.map(([value, label]) => ({ value, label }));

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1).padStart(2, '0'),
}));

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => ({
  value: String(currentYear + i),
  label: String(currentYear + i),
}));

interface PaymentMethodFormProps {
  paymentMethod: IBillingAccountViewData;
  onSubmit: (values: PaymentMethodFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  authError: string | null;
}

export function PaymentMethodForm({
  paymentMethod,
  onSubmit,
  onCancel,
  isPending,
  authError,
}: PaymentMethodFormProps) {
  const { control, handleSubmit } = useForm<PaymentMethodFormValues>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      fullName: paymentMethod.fullName,
      orgName: paymentMethod.orgName ?? '',
      address1: paymentMethod.address1,
      city: paymentMethod.city,
      region: paymentMethod.region,
      postalCode: paymentMethod.postalCode,
      countryIso2: paymentMethod.countryIso2,
      cardNum: paymentMethod.cardDetails?.cardNum ?? '',
      cardMonth: paymentMethod.cardDetails?.cardMonth ?? 1,
      cardYear: paymentMethod.cardDetails?.cardYear ?? currentYear,
      cvv: '',
    },
  });

  const isUS = useController({ name: 'countryIso2', control }).field.value === 'US';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="md">
        {authError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Payment Error">
            {authError}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <ControlledTextInput control={control} name="fullName" label="Full Name" autoComplete="cc-name" maxLength={BILLING_CONSTRAINTS.FULL_NAME_MAX} required />
          <ControlledTextInput control={control} name="orgName" label="Organization Name" autoComplete="organization" maxLength={BILLING_CONSTRAINTS.ORG_NAME_MAX} />
        </SimpleGrid>

        <ControlledTextInput control={control} name="address1" label="Address" autoComplete="address-line1" maxLength={BILLING_CONSTRAINTS.ADDRESS_MAX} required />

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <ControlledTextInput control={control} name="city" label="City" autoComplete="address-level2" maxLength={BILLING_CONSTRAINTS.CITY_MAX} required />
          <ControlledTextInput control={control} name="region" label={isUS ? 'State' : 'Region'} autoComplete="address-level1" maxLength={BILLING_CONSTRAINTS.REGION_MAX} required />
          <ControlledTextInput control={control} name="postalCode" label={isUS ? 'Zip Code' : 'Postal Code'} autoComplete="postal-code" maxLength={BILLING_CONSTRAINTS.POSTAL_CODE_MAX} required />
        </SimpleGrid>

        <ControlledSelect control={control} name="countryIso2" label="Country" data={COUNTRY_OPTIONS} required />

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <ControlledTextInput control={control} name="cardNum" label="Card Number" autoComplete="cc-number" required />
          <Group grow>
            <ControlledNativeSelect control={control} name="cardMonth" label="Month" data={MONTH_OPTIONS} autoComplete="cc-exp-month" required />
            <ControlledNativeSelect control={control} name="cardYear" label="Year" data={YEAR_OPTIONS} autoComplete="cc-exp-year" required />
          </Group>
          <ControlledTextInput control={control} name="cvv" label="CVV" autoComplete="cc-csc" maxLength={BILLING_CONSTRAINTS.CVV_MAX} required />
        </SimpleGrid>

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            Save Payment Method
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/** Generic controlled TextInput for RHF + Mantine. */
function ControlledTextInput<T extends FieldValues>({
  control,
  name,
  label,
  autoComplete,
  maxLength,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  autoComplete?: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <TextInput
      ref={field.ref}
      name={field.name}
      label={label}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      autoComplete={autoComplete}
      maxLength={maxLength}
      withAsterisk={required}
    />
  );
}

/** Generic controlled Select for RHF + Mantine. */
function ControlledSelect<T extends FieldValues>({
  control,
  name,
  label,
  data,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  data: { value: string; label: string }[];
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <Select
      ref={field.ref}
      label={label}
      data={data}
      value={String(field.value ?? '')}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      searchable
      withAsterisk={required}
    />
  );
}

/** Controlled native <select> for numeric fields — required for browser card-expiry autofill. */
function ControlledNativeSelect<T extends FieldValues>({
  control,
  name,
  label,
  data,
  autoComplete,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  data: { value: string; label: string }[];
  autoComplete?: string;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <NativeSelect
      ref={field.ref}
      name={field.name}
      label={label}
      data={data}
      value={String(field.value ?? '')}
      onChange={(event) => field.onChange(Number(event.currentTarget.value))}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      autoComplete={autoComplete}
      withAsterisk={required}
    />
  );
}
